import React, { useState, useRef, useEffect } from 'react';
import { Send, Paperclip, Smile, Image as ImageIcon, Sparkles, X, Check, Loader2 } from 'lucide-react';
import * as api from '../api';
import compressImage from '../utils/compressImage';

const FilePreview = ({ file, onRemove }) => {
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (file.type?.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);

  if (previewUrl) {
    return (
      <div className="relative group">
        <img src={previewUrl} alt={file.name} className="w-20 h-20 object-cover rounded border border-slate-600" />
        <button onClick={onRemove} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity shadow-md"><X size={10} /></button>
        <div className="text-[9px] text-slate-400 mt-1 max-w-[80px] truncate text-center">{file.name}</div>
      </div>
    );
  }

  return (
    <div className="relative group flex items-center gap-1.5 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-slate-300">
      <Paperclip size={12} />
      <span className="max-w-[120px] truncate">{file.name}</span>
      <button onClick={onRemove} className="text-slate-500 hover:text-red-400 transition-colors"><X size={12} /></button>
    </div>
  );
};

const UpdatesInputBox = ({ onSend, taskId, taskFiles, taskUpdates, taskName, aiModel, members = [] }) => {
  const [text, setText] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [aiPreview, setAiPreview] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMentionPicker, setShowMentionPicker] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const commonEmojis = ['👍', '❤️', '😊', '🎉', '👏', '🔥', '✅', '📸', '🎨', '✨', '🙏', '😍', '🤩', '👌', '🆗'];

  const insertText = (insertion) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newText = text.substring(0, start) + insertion + text.substring(end);
    setText(newText);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + insertion.length, start + insertion.length);
    }, 0);
  };

  const handleSubmit = async () => {
    if (text.trim() === '' && attachedFiles.length === 0) return;
    setIsSending(true);
    try {
      await onSend(text, attachedFiles);
      setText('');
      setAttachedFiles([]);
      setIsFocused(false);
    } catch (err) {
      console.error('[UpdatesInputBox] Erro ao enviar:', err);
    } finally {
      setIsSending(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    const compressed = await Promise.all(files.map(f => compressImage(f)));
    setAttachedFiles(prev => [...prev, ...compressed]);
    e.target.value = '';
  };

  const removeAttachedFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAIGenerate = async () => {
    const userInstruction = text.trim();
    if (!userInstruction && attachedFiles.length === 0) {
      setAiPreview('[ERRO] Escreva uma instrução para a IA ou anexe imagens primeiro.');
      return;
    }
    setIsGenerating(true);
    setAiPreview(null);
    console.log('[AI] Iniciando geração com instrução do usuário...');
    console.log('[AI] Instrução:', userInstruction.substring(0, 100));
    console.log('[AI] taskName:', taskName, 'aiModel:', aiModel);
    console.log('[AI] taskFiles:', taskFiles?.length, 'taskUpdates:', taskUpdates?.length, 'attachedFiles:', attachedFiles.length);
    try {
      const updatesContext = (taskUpdates || []).slice(0, 5).map(u => `${u.authorName}: ${u.text}`).join('\n');
      const filesInfo = (taskFiles || []).map(f => f.name).join(', ');
      const context = [
        taskName ? `Tarefa: ${taskName}` : '',
        filesInfo ? `Arquivos anexados: ${filesInfo}` : '',
        attachedFiles.length > 0 ? `Novas imagens sendo enviadas: ${attachedFiles.map(f => f.name).join(', ')}` : '',
        updatesContext ? `Conversa recente:\n${updatesContext}` : ''
      ].filter(Boolean).join('\n');
      console.log('[AI] Contexto montado:', context.substring(0, 200));

      // Converter imagens anexadas para base64
      const images = [];
      for (const file of attachedFiles) {
        if (file.type?.startsWith('image/')) {
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          images.push(base64);
          console.log('[AI] Imagem convertida:', file.name, base64.length, 'chars');
        }
      }

      const result = await api.generateAIDescription(
        userInstruction || 'Descreva o conteúdo',
        context,
        'custom',
        aiModel,
        images
      );
      console.log('[AI] Resultado:', result);
      if (result.generated) {
        setAiPreview(result.generated);
      } else {
        console.warn('[AI] Sem texto gerado no resultado');
        setAiPreview('[ERRO] IA não gerou texto. Tente outro modelo.');
      }
    } catch (err) {
      console.error('[AI] Erro:', err.message);
      setAiPreview(`[ERRO] ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const approveAI = () => {
    if (aiPreview && !aiPreview.startsWith('[ERRO]')) {
      setText(aiPreview);
      setAiPreview(null);
    }
  };

  const rejectAI = () => {
    setAiPreview(null);
  };

  return (
    <div className="space-y-2">
      {aiPreview && (
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-purple-400" />
            <span className="text-xs font-medium text-purple-300">Legenda gerada por IA</span>
          </div>
          <div className="text-slate-200 text-sm whitespace-pre-line bg-black/20 rounded p-3 mb-3">{aiPreview}</div>
          <div className="flex gap-2">
            <button onClick={approveAI} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-medium transition-colors">
              <Check size={14} /> Aprovar e usar
            </button>
            <button onClick={rejectAI} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-xs font-medium transition-colors">
              <X size={14} /> Descartar
            </button>
          </div>
        </div>
      )}

      {attachedFiles.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {attachedFiles.map((file, i) => (
            <FilePreview key={i} file={file} onRemove={() => removeAttachedFile(i)} />
          ))}
        </div>
      )}

      <input type="file" ref={fileInputRef} className="hidden" multiple onChange={handleFileSelect} />

      <div className={`bg-[#181b26] border rounded-lg transition-colors duration-200 shadow-sm ${isFocused ? 'border-blue-500 ring-1 ring-blue-500/50' : 'border-slate-700'}`}>
        <textarea ref={textareaRef} value={text} onChange={(e) => { setText(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.max(100, e.target.scrollHeight) + 'px'; }} onFocus={() => setIsFocused(true)} placeholder="Escreva uma atualização, ou dê instruções para a IA (botão ✨)..." className="w-full bg-transparent text-slate-200 p-4 min-h-[100px] outline-none resize-y placeholder-slate-500 text-sm" />
        <div className="px-4 py-3 border-t border-slate-700/50 flex justify-between items-center bg-slate-800/30 rounded-b-lg">
          <div className="flex items-center gap-3 text-slate-400">
            <div className="relative">
              <button onClick={() => setShowMentionPicker(!showMentionPicker)} className="hover:text-blue-400 transition-colors" title="Mencionar">@</button>
              {showMentionPicker && (
                <div className="absolute bottom-8 left-0 bg-[#181b26] border border-slate-600 rounded-lg shadow-xl p-2 z-50 min-w-[150px]">
                  <div className="text-xs text-slate-400 mb-2 px-2">Mencionar</div>
                  {members.length > 0 ? members.map(m => (
                    <button key={m.initials} onClick={() => { insertText(`@${m.name} `); setShowMentionPicker(false); }} className="w-full text-left px-2 py-1 text-sm text-slate-200 hover:bg-blue-600/20 rounded flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-gradient-to-tr from-purple-500 to-blue-500 flex items-center justify-center text-white text-[10px] font-bold">{m.initials}</span>
                      {m.name}
                    </button>
                  )) : (
                    <div className="px-2 py-1 text-xs text-slate-500">Sem membros</div>
                  )}
                </div>
              )}
            </div>
            <button onClick={() => fileInputRef.current?.click()} className="hover:text-blue-400 transition-colors" title="Anexar ficheiro"><Paperclip size={16} /></button>
            <button onClick={() => fileInputRef.current?.click()} className="hover:text-blue-400 transition-colors" title="Adicionar Imagem"><ImageIcon size={16} /></button>
            <div className="relative">
              <button onClick={() => setShowEmojiPicker(!showEmojiPicker)} className="hover:text-blue-400 transition-colors" title="Emoji"><Smile size={16} /></button>
              {showEmojiPicker && (
                <div className="absolute bottom-8 left-0 bg-[#181b26] border border-slate-600 rounded-lg shadow-xl p-2 z-50">
                  <div className="grid grid-cols-5 gap-1">
                    {commonEmojis.map(emoji => (
                      <button key={emoji} onClick={() => { insertText(emoji); setShowEmojiPicker(false); }} className="w-8 h-8 flex items-center justify-center text-lg hover:bg-slate-700 rounded transition-colors">
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button onClick={handleAIGenerate} disabled={isGenerating} className={`hover:text-purple-400 transition-colors flex items-center gap-1 ${isGenerating ? 'text-purple-400' : ''}`} title="Gerar com IA baseado no texto digitado">
              {isGenerating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              <span className="text-[10px] font-medium">IA</span>
            </button>
          </div>
          <button onClick={handleSubmit} disabled={(text.trim() === '' && attachedFiles.length === 0) || isSending} className={`px-4 py-1.5 rounded-md text-sm font-medium flex items-center gap-2 transition-all ${(text.trim() === '' && attachedFiles.length === 0) || isSending ? 'bg-slate-700 text-slate-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'}`}>
            {isSending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Atualizar
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdatesInputBox;
